<script lang="ts">
import { z } from 'zod'
import { BundleDBModel } from '../helpers/helpers'

export enum OrderBy {
  Default = 'default',
  Modified = 'modified',
  Created = 'created',
  Size = 'size',
}

type BundleType = z.infer<typeof BundleDBModel>

const toMillis = (
  value: { toDate?: () => Date } | Date | number | null | undefined,
) => {
  if (!value) return 0
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  const date = value.toDate?.()
  return date ? date.getTime() : 0
}

const lastModifiedAscending = (a: BundleType, b: BundleType) => {
  const aDate = a.owner_editable?.modified
    ? toMillis(a.owner_editable.modified)
    : toMillis(a.metadata?.created)
  const bDate = b.owner_editable?.modified
    ? toMillis(b.owner_editable.modified)
    : toMillis(b.metadata?.created)

  return aDate - bDate
}

const dateCreatedAscending = (a: BundleType, b: BundleType) => {
  const aDate = toMillis(a.metadata?.created)
  const bDate = toMillis(b.metadata?.created)
  return aDate - bDate
}

const sizeAscending = (a: BundleType, b: BundleType) => {
  const aSize = a.metadata?.size || 0
  const bSize = b.metadata?.size || 0
  return aSize - bSize
}

const ascComparatorForBase = (baseId: OrderBy) => {
  switch (baseId) {
    case OrderBy.Default:
    case OrderBy.Modified:
      return lastModifiedAscending
    case OrderBy.Created:
      return dateCreatedAscending
    case OrderBy.Size:
    default:
      return sizeAscending
  }
}

const descFromAsc = (
  asc: (a: BundleType, b: BundleType) => number,
): ((a: BundleType, b: BundleType) => number) => {
  return (a, b) => -asc(a, b)
}

export const filterBundles = <T extends BundleType>(
  bundles: T[] | null | undefined,
  orderBy: OrderBy,
  orderAscending: boolean,
  searchTerm: string,
): T[] => {
  const list = Array.isArray(bundles) ? bundles : []
  const asc = ascComparatorForBase(orderBy)
  const useAscending = orderBy === OrderBy.Default ? false : orderAscending
  const comparator = useAscending ? asc : descFromAsc(asc)
  const sortedBundles = [...list]
  sortedBundles.sort(comparator)

  if (!searchTerm) {
    return sortedBundles
  }

  return sortedBundles.filter((bundle) => {
    const title = bundle.owner_editable?.details?.title
    return title
      ? title.toLowerCase().includes(searchTerm.toLowerCase())
      : false
  })
}
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { getStrings } from './pickerLanguage'
import {
  AdjustmentsVerticalIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'

type OrderingOption = {
  id: OrderBy
  label: string
}

const props = withDefaults(
  defineProps<{
    language: string
    searchTerm?: string
    orderBy?: OrderBy
    orderAscending?: boolean
    enableDropdown?: boolean
    defaultLabel?: string
  }>(),
  {
    searchTerm: '',
    orderBy: OrderBy.Modified,
    orderAscending: false,
    enableDropdown: true,
    defaultLabel: 'Default',
  },
)

const emit = defineEmits([
  'update:search-term',
  'update:orderBy',
  'update:orderAscending',
])

const languageStrings = computed(() => {
  return getStrings(props.language)
})

const orderOptions = computed<OrderingOption[]>(() => {
  const s = languageStrings.value
  return [
    { id: OrderBy.Default, label: props.defaultLabel },
    { id: OrderBy.Modified, label: s.date_modified },
    { id: OrderBy.Created, label: s.date_created },
    { id: OrderBy.Size, label: s.size },
  ]
})

const showDropdown = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const toggleDropdown = (event: Event) => {
  event.stopPropagation()
  showDropdown.value = !showDropdown.value
}

const handleClickOutside = (event: Event) => {
  const target = event.target as Node | null
  if (dropdownRef.value && target && !dropdownRef.value.contains(target)) {
    showDropdown.value = false
  }
}

const selectedBaseId = computed(() => props.orderBy)
const isDefaultSelected = computed(
  () => selectedBaseId.value === OrderBy.Default,
)
const isAscending = computed(() =>
  isDefaultSelected.value ? false : !!props.orderAscending,
)

const selectOption = (option: OrderingOption) => {
  emit('update:orderBy', option.id)
  if (option.id === OrderBy.Default) {
    emit('update:orderAscending', false)
  }
  showDropdown.value = false
}

const handleSearchSubmit = (event: Event) => {
  const form = event.currentTarget as HTMLFormElement
  form.querySelector<HTMLInputElement>('.search-input')?.blur()
}
</script>

<template>
  <form
    class="search-filter-container"
    v-on-click-outside="handleClickOutside"
    @submit.prevent="handleSearchSubmit">
    <div class="main-container">
      <div class="content-wrapper">
        <MagnifyingGlassIcon class="search-icon" />
        <input
          type="text"
          :value="searchTerm"
          @input="
            emit(
              'update:search-term',
              ($event.target as HTMLInputElement).value,
            )
          "
          :placeholder="languageStrings.search_text"
          class="search-input" />
        <span class="filter-capsule"></span>
        <!--filter icon-->
        <AdjustmentsVerticalIcon
          class="filter-icon"
          @click="toggleDropdown"
          v-if="props.enableDropdown" />
      </div>
    </div>
    <!-- Dropdown menu -->
    <div
      v-if="showDropdown"
      class="dropdown"
      ref="dropdownRef">
      <h4>{{ languageStrings.order_label }}</h4>
      <div
        class="segmented"
        role="group"
        aria-label="Order direction">
        <button
          type="button"
          class="segment"
          :class="{ selected: isAscending }"
          :disabled="isDefaultSelected"
          @click="() => emit('update:orderAscending', true)">
          {{ languageStrings.ascending }}
        </button>
        <button
          type="button"
          class="segment"
          :class="{ selected: !isAscending }"
          :disabled="isDefaultSelected"
          @click="() => emit('update:orderAscending', false)">
          {{ languageStrings.descending }}
        </button>
      </div>
      <label
        v-for="option in orderOptions"
        :key="option.id"
        :for="option.id">
        <div class="option">
          <input
            type="radio"
            name="option"
            :id="option.id"
            :value="option.id"
            :checked="option.id === selectedBaseId"
            @change="selectOption(option)" />
          {{ option.label }}
        </div>
      </label>
    </div>
  </form>
</template>

<style scoped>
.search-filter-container {
  position: relative;
  width: 400px;
}
.main-container {
  width: 100%;
  height: 44px;
  border-bottom: 1px solid var(--background-reverse);
  display: flex;
  color: var(--background-reverse);
  transition-duration: 0.3s;
}
.content-wrapper {
  display: flex;
  width: 100%;
  padding: 0rem 2rem;
  justify-content: space-between;
}
.search-input {
  width: 100%;
  border: none;
  outline: none;
  padding: 0rem 0.5rem;
  background-color: var(--transparent);
  color: var(--text-color);
  font-family: var(--title-font);
}

.main-container:focus-within {
  border-bottom: 1px solid var(--text-color);
}

.main-container:focus-within .search-icon {
  color: var(--text-color);
}

.filter-icon:active {
  color: var(--text-color);
}

svg {
  width: 32px;
}
.filter-icon {
  cursor: pointer;
}
.dropdown {
  position: absolute;
  top: 100%;
  left: 1.5rem;
  border: 1px solid var(--text-color);
  border-radius: 4px;
  margin-top: 0.5rem;
  background-color: var(--background-color);
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.695);
  z-index: 10;
  padding: 1rem;
  width: 80%;
  font-family: var(--title-font);
}
.dropdown h4 {
  margin-left: 1rem;
  margin-top: 0rem;
  margin-bottom: 0;
  user-select: none;
}
.option {
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}
.option:hover {
  outline: 0.5px solid var(--background-reverse);
}
.option input[type='radio'] {
  appearance: none;
  min-width: 16px;
  height: 16px;
  border: 2px solid var(--background-reverse);
  border-radius: 0.5rem;
  position: relative;
  outline: none;
  cursor: pointer;
  margin-right: 1rem;
}
.option input[type='radio']:checked {
  background-color: var(--primary-color);
  border-color: var(--background-reverse);
}

.segmented {
  display: flex;
  border: 1px solid var(--background-reverse);
  border-radius: 6px;
  overflow: hidden;
  margin: 0.5rem 1rem;
}
.segment {
  flex: 1;
  padding: 0.25rem 0.75rem;
  background: transparent;
  color: var(--text-color);
  border: none;
  cursor: pointer;
  font-family: var(--title-font);
}
.segment + .segment {
  border-left: 1px solid var(--background-reverse);
}
.segment.selected {
  background: var(--primary-color);
  color: var(--white);
}
.segment:disabled {
  cursor: default;
  opacity: 0.5;
}
.segment:not(.selected):not(:disabled):hover {
  background: rgba(127, 127, 127, 0.15);
}

@media screen and (max-width: 768px) {
  .search-filter-container {
    width: 90%;
  }

  .content-wrapper {
    padding: 0rem 1rem;
  }
}
</style>
